import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Dropdown from "../../inputs/Dropdown/Dropdown";
import Link from "../../Link/Link";
import Chevron from "../../Chevron/Chevron";
import List from "../../List/List";
import Icon from "../../Icon/Icon";
import { useGlobalValue } from "../../../state/GlobalProvider";
import { hydrateRoute } from "../../../utils";
import styles from "./MobileMenu.module.sass";
import MobileMenuEntry from "./MobileMenuEntry";

const filterOrganizations = (organizations, filter) => {
  const normalizedFilterValue = filter && filter.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filterRegex = new RegExp(`.*${normalizedFilterValue.replace(/\*/g, "\\*").replace(/\s/gi, ".*")}.*`, "ig");
  const filteredOrganizations = organizations.map(organization => {
    const organizationNormalizedName = (organization.name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const filteredProjects = (organization.projects || []).filter(organizationProject => {
      const normalizedName = organizationProject.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const projectId = organizationProject.taxRegistrationNumber || "";
      return normalizedName.match(filterRegex) || projectId.match(filterRegex);
    });

    if (filteredProjects.length > 0) {
      return { ...organization,
        projects: filteredProjects
      };
    } else if (organizationNormalizedName.match(filterRegex)) {
      return organization;
    }
  }).filter(org => org);
  return filteredOrganizations;
};

const populateProjectItems = (organizations, project, filter) => {
  const filteredOrganization = filterOrganizations(organizations, filter);
  const projectItems = [];
  const sorted = filteredOrganization.map(organization => {
    return { ...organization,
      projects: (organization.projects || []).sort(({
        name: a
      }, {
        name: b
      }) => a.localeCompare(b))
    };
  }).sort(({
    name: a
  }, {
    name: b
  }) => a.localeCompare(b));

  if (sorted && sorted.length) {
    sorted.forEach(organization => {
      if (organization.projects && organization.projects.length) {
        projectItems.push({
          type: "organization",
          name: organization.name
        });
        organization.projects.forEach(organizationProject => {
          projectItems.push({
            type: "project",
            id: organizationProject.id,
            name: `${organizationProject.taxRegistrationNumber ? `${organizationProject.taxRegistrationNumber} | ` : ""}${organizationProject.name || ""}`,
            isSelected: organizationProject.id === project.id,
            fiscalYears: (organizationProject.fiscalYears || []).sort((a, b) => parseInt(b) - parseInt(a))
          });
        });
      }
    });
  }

  return projectItems;
};

function iterateNavigation(navigation, arrayOfNavigations, params) {
  const mergeTo = navigation.map(item => {
    const {
      navigation: innerNavigation
    } = item;

    if (innerNavigation) {
      iterateNavigation(innerNavigation, arrayOfNavigations, params);
    }

    return hydrateRoute(item.to, params);
  });
  arrayOfNavigations.push(...(mergeTo || []));
}

const renderNavigation = (navigation, params, location, closeMenu, lastLevel = -1) => {
  return navigation.map((item, index) => {
    const {
      name,
      to,
      icon,
      badge,
      exact,
      type,
      navigation: innerNavigation,
      funcOnClick,
      level
    } = item;
    let arrayOfNavigations = [];
    let childrenSize = 0;

    if (type === "separator") {
      return /*#__PURE__*/React.createElement("span", {
        key: `separator-${index}`,
        className: styles["entry-separator"]
      });
    }

    const link = hydrateRoute(to, params);

    if (innerNavigation) {
      iterateNavigation(innerNavigation, arrayOfNavigations, params);
    }

    const isExpanded = innerNavigation && innerNavigation.some(({
      to
    }) => {
      return hydrateRoute(to, params) === location.pathname;
    });
    childrenSize = arrayOfNavigations.length;
    return /*#__PURE__*/React.createElement(MobileMenuEntry, {
      onClick: closeMenu,
      key: index,
      expanded: isExpanded,
      item: {
        exact,
        icon,
        badge,
        name,
        link,
        funcOnClick
      },
      childrenSize: childrenSize,
      level: typeof level !== "undefined" ? level : lastLevel + 1
    }, innerNavigation && renderNavigation(innerNavigation, params, location, closeMenu, level));
  });
};

const MobileMenu = props => {
  const {
    closeMenu,
    logo: Logo,
    organizations,
    project,
    navigation,
    title,
    subtitle,
    home = false,
    settingsAction = false
  } = props;
  const {
    params,
    location,
    i18n,
    logout,
    setLocale,
    locales,
    locale,
    goto
  } = useGlobalValue();
  const [showProjects, setShowProjects] = useState(false);
  const [filter, setFilter] = useState("");
  const scrollContainer = useRef(null);
  const searchInput = useRef(null);
  useEffect(() => {
    if (scrollContainer.current) {
      scrollContainer.current.scrollTo(0, 0);

      if (showProjects && searchInput.current) {
        searchInput.current.focus();
      }
    }
  }, [showProjects]);
  const projectItems = populateProjectItems(organizations || [], project || {}, filter) || [];
  const currentOrganization = project ? (organizations || []).find(({
    projects
  }) => (projects || []).some(({
    id
  }) => id === project.id)) : null;
  return /*#__PURE__*/React.createElement("div", {
    className: styles["root"]
  }, /*#__PURE__*/React.createElement("button", {
    className: styles["close-button"],
    onClick: closeMenu
  }), /*#__PURE__*/React.createElement("div", {
    className: styles["menu"]
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["menu-header"]
  }, currentOrganization && /*#__PURE__*/React.createElement("div", {
    className: `${styles["current-project-container"]} ${styles["with-link"]}`
  }, Logo && /*#__PURE__*/React.createElement(RouterLink, {
    to: `${locale ? `/${locale}` : ""}/`,
    className: styles["logo-container"],
    onClick: closeMenu
  }, /*#__PURE__*/React.createElement(Logo, {
    className: styles["logo"]
  })), /*#__PURE__*/React.createElement("div", {
    onClick: () => {
      setShowProjects(!showProjects);
    }
  }, currentOrganization && /*#__PURE__*/React.createElement("h5", {
    className: styles["title"]
  }, currentOrganization.name), project && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("small", {
    className: styles["subtitle"]
  }, project.name))), /*#__PURE__*/React.createElement(Chevron, {
    className: styles["arrow"],
    direction: showProjects ? "down" : "right"
  })), !currentOrganization && title ? /*#__PURE__*/React.createElement("div", {
    className: styles["current-project-container"]
  }, Logo && /*#__PURE__*/React.createElement(RouterLink, {
    to: `${locale ? `/${locale}` : ""}/`,
    className: styles["logo-container"],
    onClick: closeMenu
  }, /*#__PURE__*/React.createElement(Logo, {
    className: styles["logo"]
  })), /*#__PURE__*/React.createElement("div", null, title && /*#__PURE__*/React.createElement("h5", {
    className: styles["title"]
  }, title), subtitle && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("small", {
    className: styles["subtitle"]
  }, subtitle)))) : null), /*#__PURE__*/React.createElement("div", {
    className: styles["options-container"],
    ref: scrollContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles["projects-container"],
    style: {
      height: showProjects ? `${projectItems.length * 34 + 20 + 36 + (!projectItems.length ? 30 : 0)}px` : "0",
      paddingBottom: showProjects ? "20px" : "0"
    }
  }, showProjects && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
    ref: searchInput,
    className: styles["search-input"],
    type: "search",
    placeholder: i18n`Search`,
    value: filter,
    onChange: e => {
      setFilter(e.target.value);
    }
  }), /*#__PURE__*/React.createElement(List, {
    handleNoResults: false,
    items: projectItems,
    onClick: e => {
      const itemType = (e.target.value || {}).type;

      if (itemType === "project") {
        setShowProjects(false);
      }
    },
    card: ({
      data
    }) => {
      const {
        id,
        name,
        type,
        isSelected,
        fiscalYears
      } = data;

      if (type === "organization") {
        return /*#__PURE__*/React.createElement("div", {
          className: styles["organization-entry"]
        }, /*#__PURE__*/React.createElement("small", {
          className: styles["title"]
        }, name));
      }

      const projectPath = `${locale ? `/${locale}` : ""}/${id}/${fiscalYears && fiscalYears.length > 0 ? Math.max(...fiscalYears) : "repository"}`;
      return /*#__PURE__*/React.createElement("div", {
        className: `${styles["project-entry"]} ${isSelected ? styles["is-selected"] : ""}`
      }, isSelected ? /*#__PURE__*/React.createElement(Icon, {
        name: "checkmark"
      }) : "", /*#__PURE__*/React.createElement(Link, {
        to: projectPath,
        className: styles["title"],
        label: name
      }));
    }
  }), filter && filter.length && !projectItems.length ? /*#__PURE__*/React.createElement("div", {
    className: styles["placeholder"]
  }, /*#__PURE__*/React.createElement("small", null, i18n`No results matching`)) : null)), /*#__PURE__*/React.createElement("nav", {
    className: styles["navigation"]
  }, renderNavigation(navigation || [], params, location, closeMenu)))), /*#__PURE__*/React.createElement("div", {
    className: styles["footer-options"]
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("button", {
    className: styles["logout-button"],
    onClick: logout
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "logout"
  }), /*#__PURE__*/React.createElement("small", null, /*#__PURE__*/React.createElement("strong", null, i18n`Logout`)))), settingsAction ? /*#__PURE__*/React.createElement("button", {
    key: "settings",
    className: styles['action-button'],
    onClick: () => {
      goto && goto(`settings`);
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "settings"
  })) : null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Dropdown, {
    className: styles["locales-button"],
    options: locales || [],
    value: locale || "en",
    i18n: i18n,
    onChange: e => {
      setLocale && setLocale(e.target.value.id);
    }
  })))));
};

export default MobileMenu;